"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.thorchainApi = void 0;
const caip_1 = require("@shapeshiftoss/caip");
const chain_adapters_1 = require("@shapeshiftoss/chain-adapters");
const unchained_client_1 = require("@shapeshiftoss/unchained-client");
const utils_1 = require("@shapeshiftoss/utils");
const assert_1 = __importDefault(require("assert"));
const axios_1 = __importDefault(require("axios"));
const viem_1 = require("viem");
const thorchain_utils_1 = require("../../thorchain-utils");
const utils_2 = require("../../utils");
const helpers_1 = require("../utils/helpers/helpers");
const constants_1 = require("./constants");
const getThorTxData_1 = require("./evm/utils/getThorTxData");
const getTradeQuote_1 = require("./getThorTradeQuote/getTradeQuote");
const getLatestThorTxStatusMessage_1 = require("./utils/getLatestThorTxStatusMessage");
const longTailHelpers_1 = require("./utils/longTailHelpers");
const parseThorBuyTxHash_1 = require("./utils/parseThorBuyTxHash");
const getThorTxData_2 = require("./utxo/utils/getThorTxData");
const deductOutboundRuneFee = (fee) => {
    // 0.02 RUNE is automatically charged on outbound transactions
    // the returned is the difference of any additional fee over the default 0.02 RUNE (ie. tx.fee >= 2000001)
    const feeMinusAutomaticOutboundFee = (0, utils_1.bnOrZero)(fee).minus(constants_1.THORCHAIN_OUTBOUND_FEE_RUNE_THOR_UNIT);
    return feeMinusAutomaticOutboundFee.gt(0) ? feeMinusAutomaticOutboundFee.toString() : '0';
};
exports.thorchainApi = {
    getTradeQuote: async (input, deps) => {
        const { affiliateBps } = input;
        return await (0, getTradeQuote_1.getThorTradeQuote)({
            ...input,
            affiliateBps,
        }, deps);
    },
    getUnsignedEvmTransaction: async ({ chainId, from, tradeQuote, supportsEIP1559, assertGetEvmChainAdapter, config, }) => {
        // TODO: pull these from db using id so we don't have type zoo and casting hell
        const { router, vault, aggregator, data, steps, memo: tcMemo, tradeType, expiry, longtailData, slippageTolerancePercentageDecimal, } = tradeQuote;
        const { sellAmountIncludingProtocolFeesCryptoBaseUnit, sellAsset } = steps[0];
        const value = (0, helpers_1.isNativeEvmAsset)(sellAsset.assetId)
            ? sellAmountIncludingProtocolFeesCryptoBaseUnit
            : '0';
        switch (tradeType) {
            case longTailHelpers_1.TradeType.L1ToL1: {
                const feeData = await chain_adapters_1.evm.getFees({
                    adapter: assertGetEvmChainAdapter(chainId),
                    data,
                    to: router,
                    value,
                    from,
                    supportsEIP1559,
                });
                return {
                    chainId: Number((0, caip_1.fromChainId)(chainId).chainReference),
                    data,
                    from,
                    to: router,
                    value,
                    ...feeData,
                };
            }
            case longTailHelpers_1.TradeType.LongTailToL1: {
                (0, assert_1.default)(aggregator, 'aggregator required for thorchain longtail to l1 swaps');
                const swapInAbiItem = (0, viem_1.parseAbiItem)('function swapIn(address tcRouter, address tcVault, string tcMemo, address token, uint256 amount, uint256 amountOutMin, uint256 deadline)');
                const expectedAmountOut = longtailData?.longtailToL1ExpectedAmountOut ?? 0n;
                // Paranoia assertion - expectedAmountOut should never be 0 as it would likely lead to a loss of funds.
                (0, assert_1.default)(expectedAmountOut > 0n, 'expected expectedAmountOut to be a positive amount');
                const amountOutMin = BigInt((0, utils_1.bnOrZero)(expectedAmountOut.toString())
                    .times((0, utils_1.bn)(1).minus(slippageTolerancePercentageDecimal ?? 0))
                    .toFixed(0, utils_1.BigNumber.ROUND_UP));
                // Paranoia: ensure we have this to prevent sandwich attacks on the first step of a LongtailToL1 trade.
                (0, assert_1.default)(amountOutMin > 0n, 'expected expectedAmountOut to be a positive amount');
                const tcRouter = router;
                const tcVault = vault;
                const token = (0, caip_1.fromAssetId)(sellAsset.assetId).assetReference;
                const amount = BigInt(sellAmountIncludingProtocolFeesCryptoBaseUnit);
                const currentTimestamp = BigInt(Math.floor(Date.now() / 1000));
                const tenMinutes = BigInt(600);
                const deadline = currentTimestamp + tenMinutes;
                const params = [tcRouter, tcVault, tcMemo, token, amount, amountOutMin, deadline];
                const swapInData = (0, viem_1.encodeFunctionData)({
                    abi: [swapInAbiItem],
                    functionName: 'swapIn',
                    args: params,
                });
                const feeData = await chain_adapters_1.evm.getFees({
                    adapter: assertGetEvmChainAdapter(chainId),
                    data: swapInData,
                    to: aggregator,
                    value,
                    from,
                    supportsEIP1559,
                });
                return {
                    chainId: Number((0, caip_1.fromChainId)(chainId).chainReference),
                    data: swapInData,
                    from,
                    to: aggregator,
                    value,
                    ...feeData,
                };
            }
            case longTailHelpers_1.TradeType.L1ToLongTail:
                const expectedAmountOut = longtailData?.L1ToLongtailExpectedAmountOut ?? 0n;
                // Paranoia assertion - expectedAmountOut should never be 0 as it would likely lead to a loss of funds.
                (0, assert_1.default)(expectedAmountOut > 0n, 'expected expectedAmountOut to be a positive amount');
                const { data: dataWithAmountOut, router: updatedRouter } = await (0, getThorTxData_1.getThorTxInfo)({
                    sellAsset,
                    sellAmountCryptoBaseUnit: sellAmountIncludingProtocolFeesCryptoBaseUnit,
                    memo: tcMemo,
                    expiry,
                    config,
                });
                (0, assert_1.default)(router, 'router required for l1 to thorchain longtail swaps');
                const feeData = await chain_adapters_1.evm.getFees({
                    adapter: assertGetEvmChainAdapter(chainId),
                    data: dataWithAmountOut,
                    to: updatedRouter,
                    value,
                    from,
                    supportsEIP1559,
                });
                return {
                    chainId: Number((0, caip_1.fromChainId)(chainId).chainReference),
                    data: dataWithAmountOut,
                    from,
                    to: updatedRouter,
                    value,
                    ...feeData,
                };
            case longTailHelpers_1.TradeType.LongTailToLongTail:
                throw Error(`Unsupported trade type: ${longTailHelpers_1.TradeType}`);
            default:
                return (0, utils_1.assertUnreachable)(tradeType);
        }
    },
    getUnsignedUtxoTransaction: async ({ tradeQuote, xpub, accountType, assertGetUtxoChainAdapter, config, }) => {
        // TODO: pull these from db using id so we don't have type zoo and casting hell
        const { steps, memo } = tradeQuote;
        const { accountNumber, sellAmountIncludingProtocolFeesCryptoBaseUnit, sellAsset, feeData } = steps[0];
        const { vault, opReturnData } = await (0, getThorTxData_2.getThorTxInfo)({
            sellAsset,
            xpub,
            memo,
            config,
        });
        return assertGetUtxoChainAdapter(sellAsset.chainId).buildSendApiTransaction({
            value: sellAmountIncludingProtocolFeesCryptoBaseUnit,
            xpub: xpub,
            to: vault,
            accountNumber,
            // skip address validation for thorchain vault addresses as they may exceed the risk score threshold, but are still valid for use
            skipToAddressValidation: true,
            chainSpecific: {
                accountType,
                opReturnData,
                // TODO: split up getTradeQuote into separate function per chain family to negate need for cast
                satoshiPerByte: feeData.chainSpecific.satsPerByte,
            },
        });
    },
    getUnsignedCosmosSdkTransaction: async ({ tradeQuote, chainId, from, config, }) => {
        // TODO: pull these from db using id so we don't have type zoo and casting hell
        const { steps, memo } = tradeQuote;
        const { sellAmountIncludingProtocolFeesCryptoBaseUnit, sellAsset, feeData } = steps[0];
        // TODO: split up getTradeQuote into separate function per chain family to negate need for cast
        const gas = feeData.chainSpecific.estimatedGasCryptoBaseUnit;
        const networkFee = feeData.networkFeeCryptoBaseUnit ?? '0';
        const { fee, msg, account } = await (async () => {
            switch (sellAsset.assetId) {
                case caip_1.thorchainAssetId: {
                    const fee = {
                        amount: [{ amount: deductOutboundRuneFee(networkFee), denom: 'rune' }],
                        gas,
                    };
                    // https://dev.thorchain.org/thorchain-dev/concepts/memos#asset-notation
                    const msg = {
                        type: chain_adapters_1.cosmossdk.ThorchainMessageType.MsgDeposit,
                        value: {
                            coins: [
                                { asset: 'THOR.RUNE', amount: sellAmountIncludingProtocolFeesCryptoBaseUnit },
                            ],
                            memo,
                            signer: from,
                        },
                    };
                    const api = new unchained_client_1.cosmossdk.thorchain.V1Api(new unchained_client_1.cosmossdk.thorchain.Configuration({
                        basePath: config.REACT_APP_UNCHAINED_THORCHAIN_HTTP_URL,
                    }));
                    const account = await api.getAccount({ pubkey: from });
                    return { fee, msg, account };
                }
                case caip_1.cosmosAssetId: {
                    const fee = {
                        amount: [{ amount: networkFee, denom: 'uatom' }],
                        gas,
                    };
                    const daemonUrl = config.REACT_APP_THORCHAIN_NODE_URL;
                    const maybeGaiaAddressData = await (0, thorchain_utils_1.getInboundAddressDataForChain)(daemonUrl, caip_1.cosmosAssetId);
                    if (maybeGaiaAddressData.isErr())
                        throw maybeGaiaAddressData.unwrapErr();
                    const gaiaAddressData = maybeGaiaAddressData.unwrap();
                    const vault = gaiaAddressData.address;
                    const msg = {
                        type: chain_adapters_1.cosmossdk.CosmosSdkMessageType.MsgSend,
                        value: {
                            amount: [{ amount: sellAmountIncludingProtocolFeesCryptoBaseUnit, denom: 'uatom' }],
                            from_address: from,
                            to_address: vault,
                        },
                    };
                    const api = new unchained_client_1.cosmossdk.cosmos.V1Api(new unchained_client_1.cosmossdk.cosmos.Configuration({
                        basePath: config.REACT_APP_UNCHAINED_COSMOS_HTTP_URL,
                    }));
                    const account = await api.getAccount({ pubkey: from });
                    return { fee, msg, account };
                }
                default:
                    throw Error(`Unsupported sellAsset.assetId '${sellAsset.assetId}'`);
            }
        })();
        return {
            chain_id: (0, caip_1.fromChainId)(chainId).chainReference,
            account_number: account.accountNumber.toString(),
            sequence: account.sequence.toString(),
            fee,
            msgs: [msg],
            memo,
        };
    },
    checkTradeStatus: async ({ txHash, chainId, accountId, fetchIsSmartContractAddressQuery, config, assertGetEvmChainAdapter, }) => {
        try {
            const maybeSafeTransactionStatus = await (0, utils_2.checkSafeTransactionStatus)({
                txHash,
                chainId,
                assertGetEvmChainAdapter,
                accountId,
                fetchIsSmartContractAddressQuery,
            });
            if (maybeSafeTransactionStatus) {
                // return any safe transaction status that has not yet executed on chain (no buyTxHash)
                if (!maybeSafeTransactionStatus.buyTxHash)
                    return maybeSafeTransactionStatus;
                // The safe buyTxHash is the on chain transaction hash (not the safe transaction hash).
                // Mutate txHash and continue with regular status check flow.
                txHash = maybeSafeTransactionStatus.buyTxHash;
            }
            const thorTxHash = txHash.replace(/^0x/, '');
            // not using monadic axios, this is intentional for simplicity in this non-monadic context
            const [{ data: txData }, { data: txStatusData }] = await Promise.all([
                axios_1.default.get(`${config.REACT_APP_THORCHAIN_NODE_URL}/lcd/thorchain/tx/${thorTxHash}`),
                axios_1.default.get(`${config.REACT_APP_THORCHAIN_NODE_URL}/lcd/thorchain/tx/status/${thorTxHash}`),
            ]);
            // We care about txStatusData errors because it drives all of the status logic.
            if ('error' in txStatusData) {
                return {
                    buyTxHash: undefined,
                    status: unchained_client_1.TxStatus.Unknown,
                    message: undefined,
                };
            }
            // We use planned_out_txs to determine the number of out txs because we don't want to derive
            // swap completion based on the length of out_txs which is populated as the trade executed
            const numOutTxs = txStatusData.planned_out_txs?.length ?? 0;
            const lastOutTx = txStatusData.out_txs?.[numOutTxs - 1];
            const buyTxHash = (0, parseThorBuyTxHash_1.parseThorBuyTxHash)(txHash, lastOutTx);
            // If thornode says the tx is done, its done ;)
            // We discard txData errors because we can derive the status before it returns a success response.
            if (!('error' in txData) && txData.observed_tx?.status === 'done') {
                return {
                    buyTxHash,
                    status: unchained_client_1.TxStatus.Confirmed,
                    message: undefined,
                };
            }
            const hasOutboundTx = lastOutTx !== undefined && lastOutTx.chain !== 'THOR';
            const message = (0, getLatestThorTxStatusMessage_1.getLatestThorTxStatusMessage)(txStatusData, hasOutboundTx);
            return {
                buyTxHash,
                status: unchained_client_1.TxStatus.Pending,
                message,
            };
        }
        catch (e) {
            console.error(e);
            return {
                buyTxHash: undefined,
                status: unchained_client_1.TxStatus.Unknown,
                message: undefined,
            };
        }
    },
};
